import React, { useId, useState } from 'react';
import { correctionErrorMessage } from '../hooks/correctionErrors';

export interface ReissueField {
  name: string;
  label: string;
  required?: boolean;
  multiline?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** Cleared whenever the named field changes (e.g. a doctor that belongs to a specialty). */
  dependsOn?: string;
}

interface ReissueDialogProps {
  title: string;
  /** Used in error messages, e.g. "la receta". */
  recordLabel: string;
  /** Human summary of the signed document that will be voided. */
  voidedSummary: string;
  /** Static, or computed from the current values (for dependent options). */
  fields: ReissueField[] | ((values: Record<string, string>) => ReissueField[]);
  initialValues: Record<string, string>;
  /** Called with the new values after every edit, e.g. to load dependent options. */
  onChange?: (values: Record<string, string>) => void;
  onSubmit: (voidReason: string, values: Record<string, string>) => Promise<unknown>;
  onCancel: () => void;
}

/**
 * Void + re-issue of a signed prescription or referral
 * (clinical-record-amendments R5, R8). One server call voids the original and
 * signs its replacement; the original stays visible in history, marked voided.
 * The dialog stays open on failure so the backend's own 403/422 is readable.
 */
const ReissueDialog: React.FC<ReissueDialogProps> = ({
  title, recordLabel, voidedSummary, fields: fieldsProp, initialValues, onChange, onSubmit, onCancel,
}) => {
  const id = useId();
  const [voidReason, setVoidReason] = useState('');
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fields = typeof fieldsProp === 'function' ? fieldsProp(values) : fieldsProp;

  const submit = async () => {
    if (!voidReason.trim()) {
      setError('Indique el motivo de la anulación.');
      return;
    }
    const missing = fields.filter((f) => f.required && !values[f.name]?.trim()).map((f) => f.label);
    if (missing.length > 0) {
      setError(`Complete los campos obligatorios: ${missing.join(', ')}.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(voidReason.trim(), values);
    } catch (err) {
      setError(correctionErrorMessage(err, recordLabel));
    } finally {
      setSaving(false);
    }
  };

  const setField = (name: string, value: string) => {
    const next = { ...values, [name]: value };
    fields.filter((f) => f.dependsOn === name).forEach((f) => { next[f.name] = ''; });
    setValues(next);
    onChange?.(next);
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={`${id}-title`}>{title}</h5>
              <button type="button" className="btn-close" aria-label="Cerrar" onClick={onCancel} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="alert alert-warning py-2">
                <div className="small fw-semibold">Se anulará (queda en el historial, no imprimible):</div>
                <div>{voidedSummary}</div>
              </div>
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <div className="mb-3">
                <label className="form-label" htmlFor={`${id}-void`}>Motivo de la anulación *</label>
                <textarea id={`${id}-void`} className="form-control" rows={2} maxLength={500}
                  value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
              </div>
              <h6>Documento de reemplazo (se emite firmado)</h6>
              <div className="row">
                {fields.map((f) => {
                  const inputId = `${id}-${f.name}`;
                  const common = { id: inputId, className: 'form-control', value: values[f.name] ?? '' };
                  return (
                    <div key={f.name} className={f.multiline ? 'col-12 mb-2' : 'col-md-6 mb-2'}>
                      <label className="form-label" htmlFor={inputId}>{f.label}{f.required ? ' *' : ''}</label>
                      {f.options ? (
                        <select {...common} onChange={(e) => setField(f.name, e.target.value)}>
                          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : f.multiline ? (
                        <textarea {...common} rows={2} onChange={(e) => setField(f.name, e.target.value)} />
                      ) : (
                        <input {...common} type="text" onChange={(e) => setField(f.name, e.target.value)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={submit} disabled={saving}>
                {saving && <i className="fa fa-spinner fa-spin me-1" aria-hidden="true" />}
                Anular y reemitir
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReissueDialog;
